<template>
  <div class="agreement-detail">
    <div ref="pdfContainer" class="pdf-container" />
  </div>
</template>

<script>
import { getAgreementPdf } from "@/apis/agreement";
import { loadJS } from "@/utils";

const PDF_JS_URL = "https://cdn.xiangshuheika.com/h5/commonjs/pdf.min.js";
const PDF_WORKER_URL =
  "https://cdn.xiangshuheika.com/h5/commonjs/pdf.worker.min.js";

let pdfJsLoading = null;

function loadPdfJs() {
  if (window.pdfjsLib) {
    return Promise.resolve(window.pdfjsLib);
  }
  if (pdfJsLoading) {
    return pdfJsLoading;
  }
  pdfJsLoading = new Promise((resolve, reject) => {
    loadJS(PDF_JS_URL, () => {
      if (!window.pdfjsLib) {
        pdfJsLoading = null;
        reject(new Error("pdf.js load failed"));
        return;
      }
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDF_WORKER_URL;
      resolve(window.pdfjsLib);
    });
  });
  return pdfJsLoading;
}

function isPdfArrayBuffer(buffer) {
  if (!buffer || !(buffer instanceof ArrayBuffer) || buffer.byteLength < 5) {
    return false;
  }
  const bytes = new Uint8Array(buffer.slice(0, 5));
  let header = "";
  for (let i = 0; i < bytes.length; i++) {
    header += String.fromCharCode(bytes[i]);
  }
  return header.startsWith("%PDF");
}

export default {
  name: "userAgreementDetail",
  async mounted() {
    await this.init();
  },
  beforeDestroy() {
    this.clearPdf();
  },
  methods: {
    async init() {
      const loading = this.$loading();
      try {
        const source = await this.resolvePdfSource();
        if (!source) {
          this.$toast("协议加载失败");
          return;
        }
        await this.renderPdf(source);
      } catch (e) {
        this.$toast("协议加载失败");
      } finally {
        loading.clear();
      }
    },
    async resolvePdfSource() {
      const { sign_id, name } = this.$route.query;
      if (!sign_id) {
        return null;
      }
      const buffer = await getAgreementPdf({
        sign_id,
        name: decodeURIComponent(name) || "",
      });
      if (!isPdfArrayBuffer(buffer)) {
        return null;
      }
      return { data: new Uint8Array(buffer) };
    },
    async renderPdf(source) {
      const pdfjsLib = await loadPdfJs();
      const container = this.$refs.pdfContainer;
      if (!container) {
        return;
      }
      this.clearPdf();
      const pdf = await pdfjsLib.getDocument(source).promise;
      const width = container.clientWidth || window.innerWidth;
      // 按设备像素比放大 canvas，避免高清屏模糊
      const outputScale = Math.min(window.devicePixelRatio || 1, 3);
      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const unscaled = page.getViewport({ scale: 1 });
        const scale = width / unscaled.width;
        const viewport = page.getViewport({ scale });
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        canvas.width = Math.floor(viewport.width * outputScale);
        canvas.height = Math.floor(viewport.height * outputScale);
        canvas.className = "pdf-page";
        canvas.style.display = "block";
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;
        canvas.style.margin = "0 auto 8px";
        canvas.style.background = "#fff";
        const transform =
          outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : null;
        container.appendChild(canvas);
        await page.render({
          canvasContext: context,
          viewport,
          transform,
        }).promise;
      }
    },
    clearPdf() {
      const container = this.$refs.pdfContainer;
      if (container) {
        container.innerHTML = "";
      }
    },
  },
};
</script>

<style lang="less" scoped>
.agreement-detail {
  min-height: 100vh;
  background: #f9fbfd;
  box-sizing: border-box;
}

.pdf-container {
  width: 100%;
  min-height: 100vh;
  box-sizing: border-box;

  /deep/ .pdf-page {
    display: block;
    margin: 0 auto 8px;
    background: #fff;
  }
}
</style>
